'use client';

import { Fragment, type ReactNode } from 'react';
import { LocaleDetails } from 'shared/src/types/languages.type';
import { MainSearchBar } from '@/components/search-bar/main-search-bar';
import { getNotFoundUiContent } from '@/lib/ui-content/not-found-ui-content';

/**
 * The recovery view's body: a slim notice and the real main search bar,
 * prefilled with what the dead URL says the visitor wanted.
 *
 * Why the full bar and not a message with links: a visitor on a dead URL is a
 * visitor with a known intent — the last URL segment IS their query. The
 * cheapest thing we can do for them is run that query through the same AI
 * workflow the home page runs, so the not-found view is the home view with
 * one line of explanation, not a dead end. Chrome (gradient, TopMenu, Footer)
 * is composed by the caller so the server not-found.tsx and the client
 * error.tsx can each bring their own flavour.
 *
 * `submitPrefill` is the server's verdict that this request looks human; the
 * bar applies its own client-side gate on top (lib/not-found/auto-search-gate).
 * With it off (crawler UA, error variant) the bar is prefilled and idle.
 */
export type RecoverySearchProps = {
  locale: LocaleDetails;
  /** Query parsed from the dead URL. Empty → generic copy, empty bar. */
  query: string;
  /** Start the search on mount for a human tab. Never set on the error variant. */
  autoSearch?: boolean;
  pg18?: boolean;
  variant?: 'not-found' | 'error';
  /** error.tsx `reset` — renders the "Try again" button when provided. */
  onRetry?: () => void;
};

/** Splits "Searching for {query} instead…" around the placeholder so the query renders emphasised. */
const renderWithQuery = (template: string, query: ReactNode): ReactNode => {
  const parts = template.split('{query}');
  if (parts.length === 1) return template;
  return parts.map((part, i) => (
    <Fragment key={i}>
      {part}
      {i < parts.length - 1 && query}
    </Fragment>
  ));
};

export const RecoverySearch = ({
  locale,
  query,
  autoSearch = false,
  pg18 = false,
  variant = 'not-found',
  onRetry,
}: RecoverySearchProps) => {
  const ui = getNotFoundUiContent(locale);
  const hasQuery = query.trim().length > 0;
  const submit = variant === 'not-found' && autoSearch && hasQuery;

  const emphasised = <span className="font-medium text-ink">“{query}”</span>;
  const title = variant === 'error' ? ui.errorTitle : ui.noticeTitle;
  const body =
    variant === 'error'
      ? ui.errorBody
      : !hasQuery
        ? ui.body
        : submit
          ? renderWithQuery(ui.noticeSearching, emphasised)
          : renderWithQuery(ui.noticePrefilled, emphasised);

  return (
    <div className="flex min-h-search flex-col max-xs:px-2 xs:px-4">
      <div
        role="status"
        data-testid="recovery-notice"
        className="mx-auto mt-3 flex w-full max-w-xl items-start gap-3 rounded-2xl border border-hairline bg-card/80 px-4 py-3 text-left shadow-sm backdrop-blur-sm sm:mt-4"
      >
        <svg
          aria-hidden
          viewBox="0 0 20 20"
          className="mt-0.5 h-4 w-4 shrink-0 text-muted"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
        >
          <circle cx="10" cy="10" r="8" />
          <path d="M10 9v5" strokeLinecap="round" />
          <circle cx="10" cy="6.4" r="0.6" fill="currentColor" stroke="none" />
        </svg>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium leading-snug text-ink">{title}</p>
          <p className="mt-0.5 text-xs leading-relaxed text-muted sm:text-[13px]">{body}</p>
        </div>
        {variant === 'error' && onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="shrink-0 self-center rounded-full border border-hairline px-3 py-1 text-xs font-medium text-ink transition-colors hover:border-accent/50"
          >
            {ui.retry}
          </button>
        )}
      </div>
      <MainSearchBar
        locale={locale}
        pg18={pg18}
        prefillQuery={hasQuery ? query : undefined}
        submitPrefill={submit}
      />
    </div>
  );
};
